import React, { useMemo, useState } from "react";
import { Alert, Button, Dropdown, FormControl, Spinner } from "react-bootstrap";
import "./PersonaRequestForm.css";

const PersonaRequestForm = ({
  clients,
  clientsLoading,
  clientsError,
  clientsSource,
  isFetching,
  onRetryClients,
  onFetch,
}) => {
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [labNo, setLabNo] = useState("");
  const [formError, setFormError] = useState("");

  const filteredClients = useMemo(() => {
    const keyword = clientSearch.trim().toLowerCase();
    if (!keyword) return clients;
    return clients.filter((client) => {
      const searchPool = [
        client?.customerName,
        client?.displayName,
        client?.clientId,
        client?.org,
        client?.center,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchPool.includes(keyword);
    });
  }, [clients, clientSearch]);

  const selectedClient = useMemo(
    () => clients.find((client) => client.clientId === selectedClientId),
    [clients, selectedClientId]
  );

  const canFetch = Boolean(selectedClientId && labNo.trim()) && !isFetching;

  const handleFetch = () => {
    setFormError("");
    if (!selectedClientId) {
      setFormError("Please select a client from the dropdown.");
      return;
    }
    if (!labNo.trim()) {
      setFormError("Please enter lab number.");
      return;
    }
    onFetch({
      clientId: selectedClientId,
      labNo: labNo.trim(),
    });
  };

  return (
    <section className="pf-request" aria-labelledby="pf-request-title">
      <div className="pf-request__heading-row">
        <h2 id="pf-request-title" className="pf-request__title">
          Persona Fetch
        </h2>
        <p className="pf-request__subtitle">Select client, add lab number, and fetch persona response.</p>
        <p className="pf-request__source">
          Client IDs source: {clientsSource === "session" ? "Admin login response (loginDetails.clientData)" : "Configured source"}
        </p>
      </div>

      {clientsError && (
        <Alert variant="warning" className="pf-request__alert">
          <div className="pf-request__alert-row">
            <span>{clientsError}</span>
            <Button size="sm" variant="outline-dark" onClick={onRetryClients}>
              Reload Session Data
            </Button>
          </div>
        </Alert>
      )}

      {formError && <Alert variant="danger">{formError}</Alert>}

      <div className="pf-request__grid">
        <div className="pf-request__field">
          <label className="pf-request__label" htmlFor="pf-client-dropdown-btn">
            Client ID Selection
          </label>
          <Dropdown>
            <Dropdown.Toggle
              id="pf-client-dropdown-btn"
              className="pf-request__dropdown-toggle"
              variant="light"
            >
              {selectedClient
                ? `${selectedClient.customerName || selectedClient.displayName} (${selectedClient.clientId})`
                : "Search and select client"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="pf-request__menu" align="start">
              <div className="pf-request__menu-search-wrap">
                <FormControl
                  autoFocus
                  placeholder="Search by name or client ID"
                  value={clientSearch}
                  onChange={(event) => setClientSearch(event.target.value)}
                />
              </div>
              <div className="pf-request__menu-list" role="listbox" aria-label="Client list">
                {clientsLoading ? (
                  <div className="pf-request__menu-empty">
                    <Spinner animation="border" size="sm" /> Loading clients...
                  </div>
                ) : filteredClients.length ? (
                  filteredClients.map((client) => (
                    <Dropdown.Item
                      key={`${client.clientId}-${client.org}`}
                      onClick={() => setSelectedClientId(client.clientId)}
                      active={client.clientId === selectedClientId}
                    >
                      <div className="pf-request__client-line-1">{client.customerName || client.displayName}</div>
                      <div className="pf-request__client-line-2">
                        {client.clientId}
                        {client.org ? ` | ${client.org}` : ""}
                        {client.center ? ` | ${client.center}` : ""}
                      </div>
                    </Dropdown.Item>
                  ))
                ) : (
                  <div className="pf-request__menu-empty">No clients found for this search.</div>
                )}
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="pf-request__field">
          <label className="pf-request__label" htmlFor="pf-lab-no-input">
            Lab Number
          </label>
          <FormControl
            id="pf-lab-no-input"
            type="text"
            placeholder="Example: AGOP220233408"
            value={labNo}
            onChange={(event) => setLabNo(event.target.value)}
          />
          <p className="pf-request__helper">Enter lab number manually after selecting the client ID.</p>
        </div>
      </div>

      <div className="pf-request__actions">
        <Button variant="dark" onClick={handleFetch} disabled={!canFetch}>
          {isFetching ? (
            <>
              <Spinner animation="border" size="sm" className="pf-request__btn-spinner" />
              Fetching Persona
            </>
          ) : (
            "Fetch Persona"
          )}
        </Button>
      </div>
    </section>
  );
};

export default PersonaRequestForm;
